import React, { useState } from 'react';
import { MonacoJsonEditor } from '../../../../components/MonacoJsonEditor';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { FieldsAndOperatorsPanel } from '../components/FieldsAndOperatorsPanel';
import styles from './CreateSegmentDialog.module.css';
import { DialogHeader } from './DialogHeader';

interface SegmentTemplateFormProps {
  segmentName: string;
  description: string;
  contentsJson: string;
  availableFields: string[];
  isLoadingFields: boolean;
  fieldsError?: Error | null;
  onSegmentNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onContentsChange: (value: string) => void;
  onValidationChange: (isValid: boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  onClose: () => void;
  isLoading: boolean;
  templateName?: string;
}

export const SegmentTemplateForm: React.FC<SegmentTemplateFormProps> = ({
  segmentName,
  description,
  contentsJson,
  availableFields,
  isLoadingFields,
  fieldsError,
  onSegmentNameChange,
  onDescriptionChange,
  onContentsChange,
  onValidationChange,
  onSubmit,
  onCancel,
  onClose,
  isLoading,
  templateName,
}) => {
  const [isJsonValid, setIsJsonValid] = useState(true);

  const handleValidationChange = (isValid: boolean) => {
    setIsJsonValid(isValid);
    onValidationChange(isValid);
  };

  const isSubmitDisabled =
    isLoading || !segmentName.trim() || !description.trim() || !isJsonValid;

  return (
    <>
      <DialogHeader
        title="Create Segment from Template"
        subtitle={
          templateName
            ? `Template: ${templateName} - Edit the conditions below to match your schema fields.`
            : 'Enter a name and description for your segment.'
        }
        onClose={onClose}
        showCloseButton
      />
      <form onSubmit={onSubmit} className={styles.templateForm}>
        <fieldset disabled={isLoading}>
          <div>
            <label htmlFor="segment-name">Segment Name</label>
            <Input
              type="text"
              id="segment-name"
              value={segmentName}
              onChange={e => onSegmentNameChange(e.target.value)}
              placeholder="Enter name for the new segment"
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="segment-description">Description</label>
            <textarea
              id="segment-description"
              value={description}
              onChange={e => onDescriptionChange(e.target.value)}
              placeholder="Enter description for the new segment"
              rows={2}
            />
          </div>

          <FieldsAndOperatorsPanel
            availableFields={availableFields}
            isLoadingFields={isLoadingFields}
            fieldsError={fieldsError}
          />

          <div>
            <label htmlFor="segment-conditions">
              Segment Conditions (JSON)
            </label>
            <p>
              Replace placeholder field names with your actual field names from
              the list above.
            </p>
            <MonacoJsonEditor
              value={contentsJson}
              onChange={onContentsChange}
              height="250px"
              readOnly={isLoading}
              onValidationChange={handleValidationChange}
              highlightPlaceholders
              validFields={availableFields}
            />
          </div>
        </fieldset>

        <footer>
          <Button type="button" onClick={onCancel} variant="outline">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitDisabled}>
            {isLoading ? 'Creating...' : 'Create Segment'}
          </Button>
        </footer>
      </form>
    </>
  );
};
